import React, { createContext, useContext, useEffect, useReducer, useState } from 'react';

import { TrackingContext } from '@gn-audio/jss-components';

import {
  FREQUENCIES,
  HearingTestAction,
  HearingTestState,
  STEP_NAMES,
  UserProfile,
} from './types/HearingTestTypes';
import type { HearingTestFields } from './types/SitecoreFields';
import { HearingTestEventAction } from './types/TrackingTypes';
import { calculateTestResults } from './utils/resultsCalculation';
import { createHearingTestEvent } from './utils/trackingHelpers';

const STORAGE_KEY = 'hearing-test-state';

function saveStateToLocalStorage(state: HearingTestState): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save hearing test state to localStorage:', error);
  }
}

function loadStateFromLocalStorage(): HearingTestState | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load hearing test state from localStorage:', error);
  }
  return null;
}

function clearStateFromLocalStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear hearing test state from localStorage:', error);
  }
}

interface HearingTestContextValue {
  state: HearingTestState;
  sitecoreFields: HearingTestFields;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: number) => void;
  updateProfile: (data: Partial<UserProfile>) => void;
  saveFrequencyScore: (ear: 'left' | 'right', frequency: number, score: number) => void;
  restartTest: () => void;
  retakeAudioTest: () => void;
  isLoading: boolean;
}

const initialState: HearingTestState = {
  currentStep: 0,
  profile: {
    gender: null,
    birthYear: null,
    hearingLevel: null,
    difficultySituations: [],
    designImportance: null,
    comfortImportance: null,
    wearingSituation: null,
  },
  testData: {
    leftEarScores: [],
    rightEarScores: [],
  },
  results: null,
  isCompleted: false,
};

function hearingTestReducer(state: HearingTestState, action: HearingTestAction): HearingTestState {
  let newState: HearingTestState;

  switch (action.type) {
    case 'NEXT_STEP':
      newState = {
        ...state,
        currentStep: state.currentStep + 1,
      };
      break;

    case 'PREVIOUS_STEP':
      newState = {
        ...state,
        currentStep: state.currentStep - 1,
      };
      break;

    case 'GO_TO_STEP':
      newState = {
        ...state,
        currentStep: action.payload,
      };
      break;

    case 'UPDATE_PROFILE':
      newState = {
        ...state,
        profile: {
          ...state.profile,
          ...action.payload,
        },
      };
      break;

    case 'SAVE_FREQUENCY_SCORE': {
      const { ear, frequency, score } = action.payload;
      const frequencies = [
        FREQUENCIES.HZ_500,
        FREQUENCIES.HZ_1000,
        FREQUENCIES.HZ_2000,
        FREQUENCIES.HZ_4000,
      ];
      const frequencyIndex = frequencies.indexOf(frequency);

      if (frequencyIndex === -1) {
        newState = state;
        break;
      }

      const scoresKey = ear === 'left' ? 'leftEarScores' : 'rightEarScores';
      const newScores = [...state.testData[scoresKey]];
      newScores[frequencyIndex] = score;

      newState = {
        ...state,
        testData: {
          ...state.testData,
          [scoresKey]: newScores,
        },
      };
      break;
    }

    case 'CALCULATE_RESULTS':
      newState = {
        ...state,
        results: action.payload,
        isCompleted: true,
      };
      break;

    case 'LOAD_STATE':
      newState = action.payload;
      break;

    case 'RETAKE_AUDIO_TEST':
      newState = {
        ...state,
        currentStep: STEP_NAMES.SOUND_CHECK,
        testData: {
          leftEarScores: [],
          rightEarScores: [],
        },
        results: null,
        isCompleted: false,
      };
      break;

    case 'RESTART_TEST':
      newState = { ...initialState };
      clearStateFromLocalStorage();
      break;

    default:
      newState = state;
  }

  if (action.type !== 'RESTART_TEST' && action.type !== 'RETAKE_AUDIO_TEST' && newState !== state) {
    saveStateToLocalStorage(newState);
  }

  return newState;
}

const HearingTestContext = createContext<HearingTestContextValue | null>(null);

export const useHearingTest = (): HearingTestContextValue => {
  const context = useContext(HearingTestContext);
  if (!context) {
    throw new Error('useHearingTest must be used within HearingTestProvider');
  }
  return context;
};

interface HearingTestProviderProps {
  children: React.ReactNode;
  sitecoreFields: HearingTestFields;
}

export const HearingTestProvider: React.FC<HearingTestProviderProps> = ({
  children,
  sitecoreFields,
}) => {
  const [state, dispatch] = useReducer(hearingTestReducer, initialState);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const stored = loadStateFromLocalStorage();
    if (stored) {
      dispatch({ type: 'LOAD_STATE', payload: stored });
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const { leftEarScores, rightEarScores } = state.testData;
    const expectedScoreCount = Object.keys(FREQUENCIES).length / 2;

    const allScoresCollected =
      leftEarScores.length === expectedScoreCount &&
      rightEarScores.length === expectedScoreCount &&
      leftEarScores.every((s) => s > 0) &&
      rightEarScores.every((s) => s > 0) &&
      !state.results;

    if (allScoresCollected) {
      const results = calculateTestResults(leftEarScores, rightEarScores, sitecoreFields);
      dispatch({ type: 'CALCULATE_RESULTS', payload: results });
    }
  }, [state.testData, state.results, sitecoreFields, isLoading]);

  const contextValue: HearingTestContextValue = React.useMemo(
    () => ({
      state,
      sitecoreFields,
      nextStep: () => dispatch({ type: 'NEXT_STEP' }),
      previousStep: () => dispatch({ type: 'PREVIOUS_STEP' }),
      goToStep: (step: number) => dispatch({ type: 'GO_TO_STEP', payload: step }),
      updateProfile: (data: Partial<UserProfile>) =>
        dispatch({ type: 'UPDATE_PROFILE', payload: data }),
      saveFrequencyScore: (ear: 'left' | 'right', frequency: number, score: number) =>
        dispatch({
          type: 'SAVE_FREQUENCY_SCORE',
          payload: { ear, frequency, score },
        }),
      restartTest: () => {
        dispatch({ type: 'RESTART_TEST' });
      },
      retakeAudioTest: () => {
        dispatch({ type: 'RETAKE_AUDIO_TEST' });
      },
      isLoading,
    }),
    [isLoading, state, sitecoreFields]
  );

  return <HearingTestContext.Provider value={contextValue}>{children}</HearingTestContext.Provider>;
};

export const useHearingTestTracking = () => {
  const { gtmEvent } = useContext(TrackingContext);

  const trackHearingTestEvent = (
    action: HearingTestEventAction | string,
    label: string | number
  ) => {
    try {
      const payload = createHearingTestEvent(action, label);
      gtmEvent(payload);
    } catch (error) {
      console.error('Failed to track hearing test event:', error);
    }
  };

  return { trackHearingTestEvent };
};
